import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { fade, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import Autocomplete from '@material-ui/lab/Autocomplete';
import InputBase from '@material-ui/core/InputBase';
import MbButton from 'components/atoms/MbButton';
import Popover from 'components/molecules/Popover';

import SnackbarWrap from 'components/organisms/Snackbar/SnackbarWrap';

const useStyles = makeStyles((theme) => ({
  filterArea: {
    position: 'relative',
    width: 500,
    padding: 15,
  },
  activeWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    minWidth: 500,
    '& > div': {
      padding: 5,
      margin: 3,
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      borderRadius: 4,
      whiteSpace: 'nowrap',
      '&:first-child': {
        marginLeft: 0,
      },
    },
  },
  root: {
    width: 221,
    fontSize: 13,
  },
  header: {
    borderBottom: '1px solid #e1e4e8',
    padding: '8px 10px',
    fontWeight: 600,
  },
  inputBase: {
    padding: 10,
    width: '100%',
    borderBottom: '1px solid #dfe2e5',
    '& input': {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: 8,
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      border: '1px solid #ced4da',
      fontSize: 14,
      '&:focus': {
        boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  paper: {
    marginTop: 10,
  },
  listbox: {
    padding: 0,
    maxHeight: 230,
  },
  popperDisablePortal: {
    position: 'static',
    overflow: 'hidden',
  },
  iconSelected: {
    width: 17,
    height: 17,
    marginRight: 5,
    marginLeft: -2,
  },
  color: {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    marginRight: 8,
    marginTop: 2,
  },
  text: {
    flexGrow: 1,
  },
  close: {
    opacity: 0.6,
    width: 18,
    height: 18,
  },
  footer: {
    // position: 'absolute',
    // bottom: 0,
    // left: 0,
    padding: '0 15px 15px',
  },
  contents: {
    height: 300,
  },
}));

let useApi = false;
let closePopover = () => {};

const MultiSelect = (props) => {
  const { title, name, list, params, callBack, dataReqPromise } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const [value, setValue] = useState([]);
  const [nameTitle, setNameTitle] = useState('');
  const [apiData, setApiData] = useState([]);
  const [snackbar, setSnackbar] = useState({
    message: '',
    state: false,
  });
  // eslint-disable-next-line

  /**
   * 초기 params에 값이 있으면 value에 값을 넣어서 초기값이 노출 될 수 있도록 변경
   */
  useEffect(() => {
    if (typeof params[name] === 'string') {
      const tempParams = params[name].split(',');
      let selectValue = [];
      for (let i = 0; i < list.length; i++) {
        if (tempParams.includes(String(list[i].value))) {
          selectValue.push(list[i]);
        }
      }
      setValue(selectValue);
    }
    // eslint-disable-next-line
  }, [list, name, params]);

  useEffect(() => {
    handleReName();
    // eslint-disable-next-line
  }, [value]);

  /**
   * list가 넘어오지않고 데이터 통신이 필요하면 API 통신하여 value에 값을 추가
   */
  useEffect(() => {
    if (typeof dataReqPromise === 'function') {
      let tempSnackbar = snackbar;
      useApi = true;
      dataReqPromise()
        .then((response) => {
          const { data } = response.data;
          setApiData(data);
        })
        .catch((error) => {
          tempSnackbar = {
            ...tempSnackbar,
            state: true,
            message: error,
          };
          setSnackbar(tempSnackbar);
        });
    }
    // eslint-disable-next-line
  }, [dataReqPromise]);

  /**
   * 필터 버튼에 노출되는 네임값을 변경하는 함수
   */
  const handleReName = useCallback(() => {
    let valueCount = value.length;
    let tempName = '';

    if (valueCount > 0) {
      tempName = value[0].name;
      if (valueCount > 1) {
        tempName = value[0].name + '외 ' + (valueCount - 1) + '개';
      }
    }
    setNameTitle(tempName);
  }, [value]);

  /**
   * 선택 후 확인버튼 클릭했을때 실행되는 함수
   */
  const handleSave = () => {
    let newParams = params;
    let tempValue;

    for (let v in value) {
      if (tempValue === undefined) {
        tempValue = value[v].value;
      } else {
        tempValue = tempValue + ',' + value[v].value;
      }
    }
    handleReName();
    if (tempValue !== undefined) {
      newParams = {
        ...newParams,
        [name]: tempValue,
      };
      callBack(newParams);
    }
    closePopover();
  };

  /**
   * 필터창 닫는 핸들링 받아오는 함수
   */
  const getOnClose = (handleClose) => {
    closePopover = handleClose;
  };

  /**
   * 스낵바 닫는 이벤트
   */
  const handleAlertClose = () => {
    setSnackbar({
      state: false,
      message: '',
    });
  };

  return (
    <>
      <Popover
        label={title}
        getOnClose={getOnClose}
        handleSave={handleSave}
        selectedLabel={nameTitle}
        filtered={nameTitle !== ''}
        filterList={
          <>
            <div className={clsx(classes.filterArea, 'clear')}>
              <div className={classes.root}>
                <div className={classes.activeWrap}>
                  {typeof value !== 'undefined' &&
                    value.map((label) => (
                      <div
                        key={label.name}
                        // style={{
                        //   backgroundColor: label.color,
                        //   color: theme.palette.getContrastText(label.color),
                        // }}
                      >
                        {label.name}
                      </div>
                    ))}
                </div>
              </div>

              <div className={classes.header}>항목을 선택하세요</div>
              <div className={classes.contents}>
                <Autocomplete
                  multiple
                  classes={{
                    paper: classes.paper,
                    listbox: classes.listbox,
                    popperDisablePortal: classes.popperDisablePortal,
                  }}
                  value={value}
                  onChange={(event, newValue) => {
                    setValue(newValue);
                  }}
                  disableCloseOnSelect={true}
                  disablePortal={true}
                  selectOnFocus
                  noOptionsText="No labels"
                  renderInput={(params) => (
                    <InputBase
                      ref={params.InputProps.ref}
                      inputProps={params.inputProps}
                      placeholder="클릭해주세요."
                      autoFocus={true}
                      className={classes.inputBase}
                    />
                  )}
                  renderOption={(option, { selected }) => (
                    <>
                      <DoneIcon
                        className={classes.iconSelected}
                        style={{ visibility: selected ? 'visible' : 'hidden' }}
                      />
                      {option.color !== '' &&
                        typeof option.color !== 'undefined' && (
                          <span
                            className={classes.color}
                            style={{
                              backgroundColor: option.color,
                            }}
                          />
                        )}

                      <div className={classes.text}>
                        {option.name}
                        <br />
                        {option.description}
                      </div>
                      <CloseIcon
                        className={classes.close}
                        style={{ visibility: selected ? 'visible' : 'hidden' }}
                      />
                    </>
                  )}
                  options={useApi ? apiData : list}
                  getOptionLabel={(option) => option.name}
                />
              </div>
            </div>
            <div className={clsx(classes.footer, 'clear')}>
              <MbButton onClick={handleSave} style={{ float: 'right' }}>
                확인
              </MbButton>
            </div>
          </>
        }
        popoverHeight={540}
        createOnOpen
      />

      <SnackbarWrap
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={snackbar.state}
        autoHideDuration={2000}
        onClose={handleAlertClose}
        variant="error"
        message={
          snackbar.message === undefined
            ? t(snackbar.message)
            : snackbar.message
        }
      />
    </>
  );
};

export default MultiSelect;
